
import crmService from "@/modules/CRM/services/crmService";

const POLL_INTERVAL = 60000;

export default {
  name: "CrmTaskReminder",
  data() {
    return {
      dismissed: {},
      polling: null,
      reminders: [],
      saving: false,
      snoozeMinutes: 10,
      stopped: false,
    };
  },
  computed: {
    activeReminder() {
      return (
        this.reminders.find(
          (item) => !this.dismissed[this.reminderKey(item)]
        ) || null
      );
    },
    canComplete() {
      return (
        this.activeReminder &&
        this.activeReminder.tipo_lembrete === "atividade" &&
        this.activeReminder.pode_concluir !== false
      );
    },
    canSnooze() {
      return (
        this.activeReminder &&
        this.activeReminder.tipo_lembrete === "atividade" &&
        this.activeReminder.pode_adiar !== false
      );
    },
    reminderDate() {
      if (!this.activeReminder) {
        return null;
      }

      return (
        this.activeReminder.data_lembrete ||
        this.activeReminder.vencimento_em ||
        this.activeReminder.data_inicio ||
        this.activeReminder.created_at
      );
    },
    reminderHeader() {
      const type = this.activeReminder?.tipo_lembrete || "atividade";

      if (type === "agenda_10_min") {
        return "Agendamento em 10 min";
      }

      if (type === "notificacao_agenda_link") {
        return "Cliente agendou";
      }

      if (type === "notificacao_funil") {
        return "Novo card no funil";
      }

      return "Tarefa agora";
    },
    reminderContext() {
      if (!this.activeReminder) {
        return "";
      }

      return (
        this.activeReminder.descricao ||
        this.activeReminder.oportunidade ||
        this.activeReminder.contato ||
        "Sem oportunidade ou contato"
      );
    },
  },
  mounted() {
    this.loadReminders();
    this.polling = setInterval(() => this.loadReminders(), POLL_INTERVAL);
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    async loadReminders() {
      if (this.stopped) {
        return;
      }

      try {
        const { data } = await crmService.lembretesAtividades({
          janela_minutos: 10,
        });
        this.reminders = this.normalizeCollection(data);
      } catch (error) {
        const status = error?.response?.status;

        if (status === 401 || status === 403) {
          this.stopped = true;
          clearInterval(this.polling);
          return;
        }

        this.reminders = [];
      }
    },
    async dismissCurrent() {
      if (!this.activeReminder) {
        return;
      }

      const reminder = this.activeReminder;
      const key = this.reminderKey(reminder);

      this.$set(this.dismissed, key, true);
      this.removeReminder(key, false);

      if (reminder.id_notificacao) {
        await this.markNotificationAsRead(reminder.id_notificacao);
      }
    },
    async openReminder() {
      if (!this.activeReminder) {
        return;
      }

      const reminder = this.activeReminder;

      if (reminder.id_notificacao) {
        await this.dismissCurrent();
      }

      if (reminder.id_crm_oportunidade) {
        this.$router.push({
          name: "crmOportunidadeDetalhe",
          params: { id: reminder.id_crm_oportunidade },
          query: { origem: this.routeOrigin(reminder) },
        });
        return;
      }

      if (reminder.action) {
        this.openAction(reminder.action);
        return;
      }

      if (reminder.id_crm_agenda) {
        this.$router.push({ name: "crmAgenda" });
        return;
      }

      this.$router.push({ name: "crmTarefas" });
    },
    async completeTask() {
      if (!this.activeReminder || !this.canComplete) {
        return;
      }

      const reminder = this.activeReminder;

      this.saving = true;
      try {
        await crmService.concluirAtividade({
          id_crm_atividade: reminder.id_crm_atividade,
        });
        this.removeReminder(this.reminderKey(reminder));
        this.$toast.success("Tarefa concluída.");
      } finally {
        this.saving = false;
      }
    },
    async snoozeTask() {
      if (!this.activeReminder || !this.canSnooze) {
        return;
      }

      const reminder = this.activeReminder;
      const minutos = Math.min(
        Math.max(Number(this.snoozeMinutes || 10), 1),
        1440
      );

      this.saving = true;
      try {
        await crmService.adiarLembreteAtividade({
          id_crm_atividade: reminder.id_crm_atividade,
          minutos,
        });
        this.removeReminder(this.reminderKey(reminder));
        this.$toast.success("Lembrete adiado.");
      } finally {
        this.saving = false;
      }
    },
    removeReminder(key, clearDismissed = true) {
      this.reminders = this.reminders.filter(
        (item) => this.reminderKey(item) !== key
      );

      if (clearDismissed) {
        this.$delete(this.dismissed, key);
      }
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) {
        return data.map((item) => this.normalizeReminder(item));
      }

      if (data && Array.isArray(data.data)) {
        return data.data.map((item) => this.normalizeReminder(item));
      }

      return [];
    },
    normalizeReminder(item) {
      return {
        tipo_lembrete: item.tipo_lembrete || "atividade",
        lembrete_key:
          item.lembrete_key ||
          (item.id_crm_atividade
            ? `atividade:${item.id_crm_atividade}`
            : `reminder:${JSON.stringify(item)}`),
        ...item,
      };
    },
    reminderKey(item) {
      return (
        item?.lembrete_key || item?.id_crm_atividade || item?.id_notificacao
      );
    },
    routeOrigin(reminder) {
      if (reminder.tipo_lembrete === "agenda_10_min") {
        return "agenda";
      }

      if (reminder.tipo_lembrete === "notificacao_funil") {
        return "funil";
      }

      return "tarefas";
    },
    openAction(action) {
      const internal = String(action)
        .replace(/^https?:\/\/[^/]+/i, "")
        .replace(/^\/?#/, "");

      if (internal.startsWith("/")) {
        this.$router.push(internal);
        return;
      }

      window.location.href = action;
    },
    async markNotificationAsRead(id) {
      try {
        await crmService.marcarNotificacaoVisualizada({ id });
      } catch (error) {
        this.$toast.error(
          "Não foi possível marcar a notificação como visualizada."
        );
      }
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString("pt-BR") : "-";
    },
  },
};
